import styled from 'styled-components'
import { animated } from '@react-spring/web'
import { useSpring } from '@react-spring/core'
import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import useInterpolateColors from '../../Hooks/useInterpolateColors'
import useGetElementWidth from '../../Hooks/useGetElementWidth'
import { StepContent, Strong } from '../../theme/styles'

import { useTheme } from 'styled-components'
// @ts-ignore
import { Scrollama, Step } from 'react-scrollama'
import { throttle } from 'throttle-debounce'
import { useTranslation, Trans } from 'react-i18next'
import '../../i18n'

const getTime = () => {
  const date = new Date()
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const seconds = date.getSeconds()

  return [hours, minutes, seconds]
}
const ExampleComponent = ({ standardTime }: { standardTime?: boolean }) => {
  const isMobile = window.innerWidth < 768
  const { t, i18n } = useTranslation()
  // const [svgRef, svgWidth] = useGetElementWidth()
  // console.log(svgWidth)
  const [arrowVisible, setArrowVisible] = useState(false)
  const arrowVisibleRef = useRef(false)
  const [svgRef, svgWidth] = useGetElementWidth()
  const width =
    window.innerWidth > window.innerHeight ? window.innerHeight : svgWidth
  const requestRef = useRef<number>(0)
  const theme = useTheme()
  const interpolateColors = useInterpolateColors()
  const [spring, api] = useSpring(() => ({
    pct: 0,
  }))
  const userInteracting = useRef(false)
  const onScroll = () => {
    const scroll = window.scrollY
    api.start({
      pct: Math.min((scroll / window.innerHeight) * 4, 1),
    })
  }
  const [hoursSpring, hoursSpringApi] = useSpring(() => ({
    rotate: 270,
  }))

  const [ghostHoursSpring, ghostHoursSpringApi] = useSpring(() => ({
    rotate: 270,
  }))

  const [minutesSpring, minutesSpringApi] = useSpring(() => ({
    rotate: 0,
  }))

  const [secondsSpring, secondsSpringApi] = useSpring(() => ({
    rotate: 0,
    onChange: () => {
      // console.log(secondsSpring.rotate.get())
      if (secondsSpring.rotate.get() >= 360) {
        // console.log('INS')
        // secondsSpringApi.start({ rotate: 0, immediate: true });
      }
    },
  }))

  const frame = () => {
    const t = getTime()
    const h = 270 + ((t[0] % 12) / 12) * 360
    const m = 270 + (t[1] / 60) * 360
    const s = 270 + (t[2] / 60) * 360

    ghostHoursSpringApi.start({
      rotate: h + ((t[1] / 60) * 360) / 12,
    })

    if (!userInteracting.current) {
      hoursSpringApi.start({
        rotate: h + ((t[1] / 60) * 360) / 12,
      })
    }

    if (!userInteracting.current) {
      minutesSpringApi.start({
        rotate: m,
      })
    }

    secondsSpringApi.start({
      rotate: s,
      immediate: t[2] === 0,
    })

    requestRef.current = requestAnimationFrame(frame)
  }

  useEffect(() => {
    requestRef.current = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(requestRef.current)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [api])

  const containerColor = spring.pct.to((pct) =>
    interpolateColors(theme.backgroundColor, '#222426', pct)
  )

  const textColor = spring.pct.to((pct) =>
    interpolateColors(theme.textColor, theme.textColorInverted, pct)
  )

  const handleEnter = useCallback((e: any) => {}, [])
  const handleExit = useCallback((e: any) => {}, [])
  const onStepProgress = useCallback((e: any) => {
    userInteracting.current = true
    const t = getTime()
    const h = 270 + ((t[0] % 12) / 12) * 360

    if (e.progress > 0.25 && !arrowVisibleRef.current) {
      setArrowVisible(true)
      hoursSpringApi.start({
        rotate: h + ((t[1] / 60) * 360) / 12 - 360 / 12,
      })
    } else if (e.progress < 0.25 && arrowVisibleRef.current) {
      setArrowVisible(false)
      hoursSpringApi.start({
        rotate: h + ((t[1] / 60) * 360) / 12,
      })
    }
  }, [])

  const throttledHandleScroll = useMemo(
    () => throttle(100, onStepProgress),
    [onStepProgress]
  )

  useEffect(() => {
    arrowVisibleRef.current = arrowVisible
  }, [arrowVisible])

  // 180 + (360 - rotate + 90) % 360

  return (
    <Container style={{ backgroundColor: containerColor }}>
      <ClockContainer ref={svgRef}>
        <Clock>
          <SvgClock viewBox='0 0 200 200'>
            <HourMarks>
              {Array.from({ length: 12 }, (_, i) => (
                <animated.line
                  key={i}
                  x1='100'
                  y1='5'
                  x2='100'
                  y2='25'
                  stroke={spring.pct.to((pct) =>
                    interpolateColors(
                      theme.backgroundColorInverted,
                      '#ffffff',
                      pct
                    )
                  )}
                  stroke-width='3'
                  transform={`rotate(${i * 30}, 100, 100)`}
                />
              ))}
            </HourMarks>
          </SvgClock>
          <Arrow
            visible={arrowVisible}
            src={require(`./arrow-reverse.svg`).default}
            style={{
              transform: `translate(-50%, -50%) rotate(${
                -5 +
                ((getTime()[0] % 12) / 12) * 360 +
                ((getTime()[1] / 60) * 360) / 12
              }deg)`,
            }}
          />
        </Clock>
        <Svg
          style={{
            backgroundImage: hoursSpring.rotate.to((rotate) => {
              const t = getTime()
              const h = 270 + ((t[0] % 12) / 12) * 360

              const pct = Math.abs(
                (rotate - (h + ((t[1] / 60) * 360) / 12)) / 360
              )
              return `conic-gradient(from ${rotate + 90}deg, rgba(${
                theme.mainColor
              }, 1) ${0}deg, rgba(${theme.mainColor}, 0) ${pct * 360}deg)`
            }),
          }}
          width={width}
          height={width}
          viewBox='0 0 100 100'
        >
          <defs>
            <mask id='m1'>
              <circle
                cx={width / 2}
                cy={width / 2}
                r={isMobile ? '35vmin' : '34vmin'}
                // stroke='white'
                // stroke-width={width * 0.2}
                fill='white'
              />
            </mask>
          </defs>
        </Svg>
        <Section>
          <GhostHourhand
            style={{
              transform: ghostHoursSpring.rotate.to(
                (rotate) => `translate(0, -50%) rotate(${rotate}deg)`
              ),
            }}
          ></GhostHourhand>
          <Hourhand
            style={{
              transform: hoursSpring.rotate.to(
                (rotate) => `translate(0, -50%) rotate(${rotate}deg)`
              ),
              backgroundColor: spring.pct.to((pct) =>
                interpolateColors(theme.backgroundColorInverted, '#ffffff', pct)
              ),
            }}
          ></Hourhand>
          <Minutehand
            style={{
              transform: minutesSpring.rotate.to(
                (rotate) => `translate(0, -50%) rotate(${rotate}deg)`
              ),
              backgroundColor: spring.pct.to((pct) =>
                interpolateColors(theme.backgroundColorInverted, '#ffffff', pct)
              ),
            }}
          />
          <SecondHand
            style={{
              transform: secondsSpring.rotate.to(
                (rotate) => `translate(0, -50%) rotate(${rotate}deg)`
              ),
              backgroundColor: spring.pct.to((pct) =>
                interpolateColors(theme.tenantColor, '#ffffff', pct)
              ),
            }}
          />
          <Center
            style={{
              backgroundColor: spring.pct.to((pct) =>
                interpolateColors(theme.backgroundColorInverted, '#ffffff', pct)
              ),
            }}
          />
        </Section>
      </ClockContainer>
      <ScrollContainer>
        <Scrollama
          offset={1}
          onStepEnter={handleEnter}
          onStepExit={handleExit}
          onStepProgress={throttledHandleScroll}
        >
          <Step data={1}>
            <ScrollytellingStep>
              <StepContainer>
                <StepContent>
                  {new Date() < new Date('2025-10-27') ? (
                    <p>
                      <Trans
                        i18nKey='header:scrollytelling:p1Before'
                        values={{
                          futureTime: `${getTime()[0] - 1}${
                            i18n.language === 'fr' ? ':' : '.'
                          }${String(getTime()[1]).padStart(2, '0')}`,
                          currentTime: `${getTime()[0]}${
                            i18n.language === 'fr' ? ':' : '.'
                          }${String(getTime()[1]).padStart(2, '0')}`,
                        }}
                        components={{ Strong: <Strong /> }}
                      />
                    </p>
                  ) : (
                    <p>
                      <Trans
                        i18nKey='header:scrollytelling:p1After'
                        components={{ Strong: <Strong /> }}
                      />
                    </p>
                  )}
                  {/* <p>{t('header:scrollytelling:p2')}</p> */}
                </StepContent>
              </StepContainer>
            </ScrollytellingStep>
          </Step>
        </Scrollama>
      </ScrollContainer>
    </Container>
  )
}

const Container = styled(animated.div)`
  background: #222426;
`

const ScrollContainer = styled.div``

const ScrollytellingStep = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
`

const Arrow = styled.img<{ visible?: boolean }>`
  width: 100vmin;
  height: 100vmin;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.5s;
`

const Svg = styled(animated.svg)`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  mask: url(#m1);
`

const SvgClock = styled(animated.svg)`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70vmin;
  height: 70vmin;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 80vmin;
    height: 80vmin;
  }
`

const StepContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.initialHeight}px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-bottom: ${({ theme }) => theme.initialHeight}px;
  }
`

const ClockContainer = styled.div`
  height: ${({ theme }) => theme.initialHeight}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
`

const Section = styled.div`
  width: 80vmin;
  height: 80vmin;
  margin: auto;
  border-radius: 50%;
  position: relative;
  z-index: 1;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100vmin;
    height: 100vmin;
  }
`

const hand = `
  width: 25vmin;
  height: 2vmin;
  background: #fafbfc;
  position: absolute;
  top: 50%;
  left: calc(50% - 3.5vmin);
  z-index: 2;
  transform-origin: 3.5vmin;
  box-shadow: 12px 12px 7px rgba(0,0,0,0.5);
`

const Center = styled(animated.div)`
  background: #fafbfc;
  width: 5vmin;
  height: 5vmin;
  border-radius: 50%;
  z-index: 3;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const Hourhand = styled(animated.div)`
  ${hand}

  border-radius: 20px;
  box-shadow: -10px 0px 20px rgba(0, 0, 0, 0.4);

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 28vmin;
  }
`

const GhostHourhand = styled(Hourhand)`
  background: #3e4246;
`

const Minutehand = styled(animated.div)`
  ${hand}

  width: 36vmin;
  height: 1vmin;
  border-radius: 20px;
  box-shadow: -10px 10px 10px rgba(0, 0, 0, 0.4);

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 40vmin;
  }
`

const SecondHand = styled(animated.div)`
  ${hand}

  width: 35vmin;
  height: 0.5vmin;
  transform: rotate(160deg);
  box-shadow: -10px -10px 10px rgba(0, 0, 0, 0.4);

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 39vmin;
  }
`

const Clock = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const HourMarks = styled.g``

export default ExampleComponent
